<template>
  <div class="bg-truegray-700 min-h-screen">
    <!-- HEADING -->
    <div class="lg:h-72 flex justify-start items-end p-12 bg-gray-200">
      <div
        class="h-32 border-l-4 pl-6 border-black flex flex-col justify-end items-end m-0 p-0 w-full"
      >
        <h1 class="w-full text-left">Manage Pages</h1>
        <span class="text-md w-full text-left"
          >Customise your menu structure and add new pages.</span
        >
      </div>
    </div>

    <!-- CONTENT -->
    <div
      class="flex flex-col lg:flex-row lg:space-x-6 mx-6 lg:mx-12 py-6 lg:py-12"
    >
      <!-- CONTENT Col 1 -->
      <div
        class="bg-white rounded-xl shadow w-full lg:w-1/2 py-16 px-6 lg:p-16 relative"
      >
        <h2>Menu Structure</h2>
        <MenuOrderDraggable v-if="!ispage" />
      </div>

      <!-- CONTENT Col 2 -->
      <div
        class="bg-white rounded-xl shadow mt-6 w-full lg:w-1/2 py-16 px-6 lg:p-16 lg:-mt-40 relative"
      >
        <h2 class="mb-8">Add Pages</h2>

        <div class="">
          <button class="btn btn-black" @click="newPage">New page</button>
        </div>

        <button
          :disabled="!canSubmit || pageThinking"
          :class="[
            !canSubmit
              ? 'opacity-25 cursor-not-allowed border border-truegray-700'
              : 'btn-primary',
            pageThinking ? 'thinking-primary cursor-not-allowed' : '',
          ]"
          type="submit"
          class="btn absolute right-16 top-32"
          @click="submitUpdates"
        >
          <div class="relative">
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie v-if="pageThinking" :options="loadingAnimationOptions" />
            </div>
            <span :class="[pageThinking ? 'invisible' : '']">Submit</span>
          </div>
        </button>

        <h3 class="mt-12 mb-8">Add Dropdown Label or External Link</h3>
        <!-- Page type -->
        <div :class="inputWrapperClass" class="h-12 w-60">
          <!-- Label -->
          <h6 :class="inputLabelClass">Type (required)</h6>
          <!-- Select -->
          <button
            :class="ddButtonClass"
            @click="ddPageTypeActive = !ddPageTypeActive"
          >
            <span
              v-if="page_external_link === 1 || page_external_link === 2"
              :class="[
                ddSpanClass,
                !ddPageTypeActive ? 'text-black' : 'text-truegray-300',
              ]"
            >
              {{ page_external_link_title }}
            </span>
          </button>
          <!-- Icons -->
          <svg
            v-if="!ddPageTypeActive"
            :class="ddSvgClass"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            @click="ddPageTypeActive = true"
          >
            <path
              fill-rule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            :class="ddSvgClass"
            viewBox="0 0 20 20"
            fill="currentColor"
            @click="ddPageTypeActive = false"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          <!-- Options -->
          <select
            id="page_external_link"
            v-model="page_external_link"
            name="page_external_link"
            autocomplete="page_external_link"
            class="hidden"
          >
            <option :value="2">Dropdown Label</option>
            <option :value="1">External Link</option>
          </select>
          <ul
            :class="[
              ddUlClass,
              ddPageTypeActive
                ? 'top-12 opacity-1'
                : 'top-0 opacity-0 invisible',
            ]"
          >
            <li
              v-for="(option, i) in page_types"
              :key="i"
              :class="ddLiClass"
              @click="updatePageType(option)"
            >
              {{ option.title }}
            </li>
          </ul>
        </div>
        <!-- Page type -->

        <div>
          <!-- ************ ONLY INCLUDE LINK URL AND TITLE  ************ -->

          <!-- Post Title -->
          <div :class="inputWrapperClass" class="h-12">
            <h6 :class="inputLabelClass">Title</h6>
            <input
              id="title"
              v-model="page_title"
              type="text"
              name="title"
              autocomplete="title"
              :class="inputTextClass"
            />
          </div>

          <div
            v-if="page_external_link === 1"
            :class="inputWrapperClass"
            class="h-12 -mt-4"
          >
            <h6 :class="inputLabelClass">Link URL (optional)</h6>
            <input
              id="page_link_url"
              v-model="page_link_url"
              type="text"
              name="page_link_url"
              placeholder="https://careerwise.school"
              autocomplete="page_link_url"
              :class="inputTextClass"
            />
            <p v-if="!valid_link_url" class="text-xs text-red-500">
              Please provide a valid URL
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { shared } from '../../../../js/mixins/shared.js'
import { EventBus } from '../../../../js/plugins/event-bus.js'
import MenuOrderDraggable from '../../../../shared/tenant/MenuOrderDraggable.vue'
import * as loadingAnimation from '../../../../shared/components/loading.json'

export default {
  components: {
    MenuOrderDraggable,
  },

  display: 'Nested',
  order: 15,
  mixins: [shared],

  data() {
    return {
      badbrowser: false,
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      pageThinking: false,
      loaded: false,
      ispage: false,
      canSubmit: false,
      page_status: 1,
      publish_status: true,
      page_title: '',
      page_title_original: '',
      page_body: '',
      page_body_original: '',
      page_image_featured: null,
      page_image_featured_original: null,
      previewImage: null,
      previewImageChanged: false,
      page_image_featured_remove: false,
      page_link_title: '',
      page_link_title_original: '',
      page_link_url: '',
      page_link_url_original: '',
      valid_link_url: false,
      page_external_link: null,
      page_external_link_original: null,
      page_external_link_title: null,
      thinkingDelete: false,
      mandatory_options: ['title'],
      update: ['status'], // Preload the status option...
      // dropdowns active
      ddPageTypeActive: false,
      page_types: [
        { title: 'External Link', value: 1 },
        { title: 'Dropdown Label', value: 2 },
      ],

      //input styles
      toggleClass:
        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none mb-8',
      inputWrapperClass:
        'rounded relative bg-white shadow border border-truegray-300 mb-8 ',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
      inputTextAreaClass:
        'flex-grow px-4 mr-0 mt-5 outline-none focus:outline-none bg-transparent h-24 font-primary text-black  focus:placeholder-truegray-300 transition-all duration-100 ease-in-out w-full',
      ddButtonClass:
        'w-full flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-12 font-primary text-black cursor-pointer',
      ddSvgClass: 'cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2',
      ddSpanClass: 'block text-base font-primary truncate text-left mt-2',
      ddUlClass:
        'no-scrollbar transform ease-in-out duration-300 max-h-56 shadow-md px-6 w-full absolute left-0 z-50 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-xl',
      ddLiClass:
        'w-full cursor-pointer border-b last:border-transparent relative flex items-center px-2 h-12',
      imgWrapperClass:
        'banner-changer w-full h-72 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20',
      imgButtonClass:
        'z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6',
      infoClass:
        'absolute left-full top-0 w-28 z-50 border border-truegray-300 rounded py-2 px-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white',
    }
  },

  watch: {
    page_external_link: {
      // // Refactored to page type. 0 = normal page, 1 = external link, 2 = label
      handler: function (val) {
        if (val === this.page_external_link_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'external_link') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else if (val === 1) {
          this.$set(this, 'mandatory_options', [])
          this.$set(this, 'mandatory_options', ['title', 'link_url'])
          if (!this.update.includes('external_link')) {
            this.update.push('external_link')
          }
        } else if (val === 2) {
          this.$set(this, 'mandatory_options', [])
          this.$set(this, 'mandatory_options', ['title'])

          if (!this.update.includes('external_link')) {
            this.update.push('external_link')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    page_title: {
      // String option
      handler: function (val) {
        if (val === this.page_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('title')) {
            this.update.push('title')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    page_link_title: {
      // String option
      handler: function (val) {
        if (val === this.page_link_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'link_title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('link_title')) {
            this.update.push('link_title')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    page_link_url: {
      // String option
      handler: function (url) {
        if (!url) {
          url = ''
        }

        if (url.length > 0) {
          if (!this.validateURL(url)) {
            this.$set(this, 'valid_link_url', false)
          } else {
            if (!this.validateURLProtocol(url)) {
              url = this.$set(
                this,
                'page_link_url',
                this.updateURLWithProtocol(url),
              )
            }
            this.$set(this, 'valid_link_url', true)
          }
        } else {
          this.$set(this, 'valid_link_url', true)
        }

        if (url === this.page_link_url_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'link_url') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('link_url')) {
            this.update.push('link_url')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },
  },

  mounted() {
    // Determine if we need to show a browser warning...
    let badbrowser = document.getElementsByTagName('body')[0].dataset.badbrowser
    if (badbrowser) {
      this.$set(this, 'badbrowser', true)
    }

    this.$set(this, 'loaded', true)
  },

  methods: {
    newPage() {
      EventBus.$emit('newPage')
    },

    resetData() {
      this.$set(this, 'canSubmit', '')
      this.$set(this, 'page_title', '')
      this.$set(this, 'page_link_url', '')
      this.$set(this, 'page_link_title', '')
      this.$set(this, 'page_title_original', '')
      this.$set(this, 'page_link_url_original', '')
      this.$set(this, 'page_link_title_original', '')
      this.$set(this, 'publish_status', true)
      this.$set(this, 'page_external_link', 0)
      this.$set(this, 'page_external_link_original', 0)
      this.$set(this, 'thinkingDelete', false)
    },

    processSubmitData() {
      let canSubmit = this.update.length > 0 ? true : false

      this.mandatory_options.forEach((i) => {
        let val = this.returnDataNodeValue(i)
        if (val === null) {
          canSubmit = 0
        }
        if (val.length < 1) {
          canSubmit = 0
        }
        if (i === 'link_url') {
          if (!this.validateURL(val)) {
            canSubmit = 0
          }
        }
      })

      if (canSubmit) {
        this.$set(this, 'canSubmit', true)
      } else {
        this.$set(this, 'canSubmit', false)
      }
    },

    changeImage() {
      this.$refs.selectImage.click()
    },

    imageSelected() {
      let input = this.$refs.selectImage
      let file = input.files

      if (file && file[0]) {
        if (file[0].size > 1024 * 1024 * 1) {
          EventBus.$emit('triggerAlertResponse', {
            status: 'error',
            message: 'File size too large. 1mb file size limit exceeded.',
          })
          return
        }

        let reader = new FileReader()

        reader.onload = (e) => {
          this.$set(this, 'previewImageChanged', true)
          this.$set(this, 'page_image_featured', file[0]) // @geoff this needs to go below previewImageChanged

          this.$set(this, 'page_image_featured_remove', false)
          // this.$set(this,'page_image_featured',e.target.result)
          this.previewImage = e.target.result
        }

        reader.readAsDataURL(file[0])
        this.$emit('input', file[0])
      }
    },

    resetImage() {
      this.$set(this, 'previewImageChanged', false)
      this.$set(this, 'page_image_featured', null)
      this.$set(this, 'previewImage', this.page_image_featured_original)
      this.$refs.selectImageForm.reset()
    },

    returnDataNodeValue(key) {
      let value = null
      switch (key) {
        case 'title':
          value = this.page_title
          break

        case 'body':
          value = this.page_body
          break

        case 'image_featured':
          value = this.page_image_featured
          break

        case 'link_title':
          value = this.page_link_title
          break

        case 'link_url':
          value = this.page_link_url
          break

        case 'status':
          value = this.page_status ? 1 : 0
          break

        case 'external_link':
          value = this.page_external_link
          break

        default:
          break
      }
      return value
    },

    submitUpdates() {
      if (!this.canSubmit) {
        return
      }

      let submitData = new FormData()
      let score = 0
      Object.entries(this.update).map(([, value]) => {
        let val = this.returnDataNodeValue(value)
        if (val !== null) {
          score = score + 1
        }
        if (val !== null) {
          submitData.append(`${value}`, val)
        }
      })

      if (score < 1) {
        return
      }

      let APIendpoint = '/api/auth/create-page'

      this.$set(this, 'pageThinking', true)

      axios.post(APIendpoint, submitData).then((response) => {
        if (response.data.success) {
          this.resetData()
          EventBus.$emit('getManagerMenu')
        }

        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }

        this.$set(this, 'pageThinking', false)
      })
    },

    updatePageType(val) {
      // refactored external link to page type. 0 = page, 1 = external link, 2 = label
      this.$set(this, 'page_external_link', val.value)
      this.$set(this, 'page_external_link_title', val.title)
      this.$set(this, 'ddPageTypeActive', false)
    },
  },
}
</script>

<style>
/*
* Style for nestable
*/
.nestable {
  position: relative;
}
.nestable-rtl {
  direction: rtl;
}
.nestable .nestable-list {
  margin: 0;
  padding: 0 0 0 40px;
  list-style-type: none;
}
.nestable-rtl .nestable-list {
  padding: 0 40px 0 0;
}
.nestable > .nestable-list {
  padding: 0;
}
.nestable-item,
.nestable-item-copy {
  margin: 10px 0 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
  margin-top: 0;
}
.nestable-item .nestable-list,
.nestable-item-copy .nestable-list {
  margin-top: 10px;
}
.nestable-item {
  position: relative;
}
.nestable-item.is-dragging .nestable-list {
  pointer-events: none;
}
.nestable-item.is-dragging * {
  opacity: 0;
  filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
  content: ' ';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(106, 127, 233, 0.274);
  border: 1px dashed rgb(73, 100, 241);
  -webkit-border-radius: 5px;
  border-radius: 5px;
}
.nestable-drag-layer {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
  left: auto;
  right: 0;
}
.nestable-drag-layer > .nestable-list {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
  padding: 0;
}
.nestable [draggable='true'] {
  /* cursor: move; */
}
.nestable-handle {
  /* display: inline; */
}
</style>
